/* eslint-disable no-underscore-dangle, dot-notation */
import debug from "debug";
import _ from "lodash";
import constants from "./constants";
import Ajv from "ajv";
import getJSONSchemaDefinition from "./json-schema-definition";
const jsonSchemaConverter = require("./json-schema-to-mongoose");

const log = debug("nqm-core-utils:schema-utils");
const errLog = debug("nqm-core-utils:schema-utils:error");

// From mongoose/lib/schema.js
const reservedFieldNames = [
  "_id", // <---- TDX specific
  "prototype",
  // EventEmitter
  "emit",
  "on",
  "once",
  "listeners",
  "removeListener",
  // document properties and functions
  "collection",
  "db",
  "errors",
  "init",
  "isModified",
  "isNew",
  "get",
  "modelName",
  "save",
  "schema",
  "set",
  "toObject",
  "validate",
  "remove",
  // hooks.js
  "_pres",
  "_posts",
  "__update", // <---- TDX specific
  "__version", // <---- TDX specific
  "__v", // <---- mongoose
  "length", // <---- causes all sorts of problems with lodash iteration
];

const vocabTypeLookupMongoose = function(vocab, id) {
  const vocabDefinition = _.find(vocab, (v) => v.id === id);
  return vocabDefinition && vocabDefinition.basedOn[0];
};

const vocabTypeLookupTDX = function(vocab, id) {
  const vocabDefinition = _.find(vocab, (v) => v.id === id);
  return vocabDefinition && vocabDefinition.basedOn.concat(vocabDefinition.id);
};

// Convert from simple array list to TDX index format.
// Assume ascending sort direction.
// e.g. ["id", "code"] => [{asc: "id"}, {asc: "code"}]
//
// Will also convert from mongoose index spec to tdx format.
// e.g. {id: 1, code: -1} => [{asc: "id"}, {desc: "code"}]
//
const indexToTDX = function(indexFields, errList) {
  const tdxIndex = [];
  errList = errList || [];

  if (Array.isArray(indexFields)) {
    _.forEach(indexFields, function(fieldSpec) {
      if (typeof fieldSpec === "string") {
        tdxIndex.push({asc: fieldSpec});
      } else if (fieldSpec.asc || fieldSpec.desc) {
        tdxIndex.push(fieldSpec);
      } else if (fieldSpec["2dsphere"]) {
        tdxIndex.push(fieldSpec);
      } else if (fieldSpec["text"]) {
        tdxIndex.push(fieldSpec);
      } else {
        errList.push(`invalid index spec: ${JSON.stringify(fieldSpec)}`);
      }
    });
  } else if (typeof indexFields === "object") {
    _.forEach(indexFields, function(sortDirection, key) {
      if (sortDirection === -1) {
        tdxIndex.push({desc: key});
      } else if (sortDirection === 1) {
        tdxIndex.push({asc: key});
      } else if (sortDirection === "2dsphere") {
        tdxIndex.push({["2dsphere"]: key});
      } else if (sortDirection === "text") {
        tdxIndex.push({["text"]: key});
      }
    });
  }

  return tdxIndex;
};

const indexesToTDX = function(indexes, errList) {
  return _.map(indexes, (idx) => {
    return indexToTDX(idx, errList);
  });
};

const indexToMongoose = function(index) {
  const mongooseIndex = {};
  _.forEach(index, function(i) {
    if (i.asc || i.desc) {
      mongooseIndex[i.asc || i.desc] = i.asc ? 1 : -1;
    } else if (i["2dsphere"]) {
      mongooseIndex[i["2dsphere"]] = "2dsphere";
    } else if (i["text"]) {
      mongooseIndex[i["text"]] = "text";
    }
  });

  return mongooseIndex;
};

const indexesToMongoose = function(indexes) {
  return _.map(indexes || [], (idx) => {
    return indexToMongoose(idx);
  });
};

const validateTDXType = function(vocab, type, errList) {
  let properType = vocabTypeLookupTDX(vocab, type);
  if (!properType) {
    type = type.toLowerCase();
    if (!constants.mongooseTypes[type]) {
      errList.push(`invalid base type: ${type}`);
    } else {
      properType = [type];
    }
  }

  return properType;
};

/**
 * Converts a schema from simple (or mongoose) format to TDX format.
 * n.b. recursive
 * @param  {} schema
 * @param  {} errList
 */
const schemaToTDX = function(vocab, schema, errList) {
  if (typeof schema === "object") {
    _.forEach(schema, function(value, key) {
      if (key === "__tdxType") {
        // Ensure array type list.
        schema.__tdxType = [].concat(value);
      } else if (key === "type") {
        // Convert to TDX format.
        if (typeof value === "string") {
          // A mongoose-style type specification, e.g. {"name": {"type": "string"}}
          // Convert to array type spec.
          schema.__tdxType = validateTDXType(vocab, value, errList);
          delete schema.type;
        } else if (Array.isArray(value)) {
          // An array type spec.
          if (value.length) {
            if (typeof value[0] === "object") {
              // A type specification for a type named 'type' as an array, groan...
              // e.g. {type: [{type: "string"}]} - an array of strings
              schema[key] = schemaToTDX(vocab, value, errList);
            } else {
              // A mixed mongoose/TDX type specification, e.g. {"name": {"type": ["string", "resourceId", "datasetId"]}}
              // A TDX-style list of vocab hierarchy => already TDX format, but validate root type
              validateTDXType(vocab, value[0], errList);
              schema.__tdxType = value;
              delete schema.type;
            }
          } else {
            // An emtpy array for the 'type' key => assume a 'mixed' mongoose type.
            schema[key] = [];
          }
        } else if (typeof value === "object") {
          // An object spec => recurse
          schema[key] = schemaToTDX(vocab, value, errList);
        } else {
          errList.push(`invalid schema definition, invalid 'type' spec: ${schema}`);
        }
      } else if (key === "__tdxRequired" || key === "__tdxDefault" || key === "__tdxDescription") {
        // Do nothing.
      } else if (Array.isArray(value)) {
        if (value.length === 1) {
          value[0] = schemaToTDX(vocab, value[0], errList);
        } else if (value.length) {
          // Multi-value array => a mongoose-style TDX vocab list, e.g. {cost: ["number", "currency"]}
          schema[key] = {__tdxType: value};
        } else {
          // Assume a 'mixed' mongoose type.
          schema[key] = [];
        }
      } else if (typeof value === "object") {
        // Recurse for sub-documents
        schema[key] = schemaToTDX(vocab, value, errList);
      } else if (typeof value === "string") {
        // An implied type spec, e.g. {name: "string"} - convert to TDX
        schema[key] = {__tdxType: validateTDXType(vocab, value, errList)};
      } else {
        errList.push(`invalid schema definition, unexpected: ${schema}`);
      }
    });

    return schema;
  } else if (typeof schema === "string") {
    return {__tdxType: validateTDXType(vocab, schema, errList)};
  } else {
    errList.push(`invalid schema definition, unexpected: ${schema}`);
  }
};

const validateMongooseType = function(vocab, type, errList) {
  let properType;
  type = vocabTypeLookupMongoose(vocab, type) || type;
  type = type.toLowerCase();
  if (!constants.mongooseTypes[type]) {
    errList.push(`invalid base type: ${type}`);
  } else {
    properType = _.capitalize(type);
  }
  return properType;
};

// Converts a schema from TDX to mongoose format.
const schemaToMongoose = function(vocab, schema, errList, forDisplay) {
  errList = errList || [];

  _.each(schema, function(v, k) {
    if (reservedFieldNames.indexOf(k) >= 0) {
      errLog("invalid field name (reserved): %s", k);
      errList.push(`invalid field name (reserved): ${k}`);
    } else if (k === "__tdxRequired") {
      schema.required = schema.__tdxRequired;
      delete schema.__tdxRequired;
    } else if (k === "__tdxDefault") {
      schema.default = schema.__tdxDefault;
      delete schema.__tdxDefault;
    } else if (k === "__tdxDescription") {
      if (forDisplay) {
        // Include the description if for display
        schema.description = schema.__tdxDescription;
      }
      // Remove this as it's TDX documentation.
      delete schema.__tdxDescription;
    } else if (k === "__tdxType") {
      // This is an explicit type specification,  e.g. { __tdxType: ["string","geographic","address","postcode"]}
      if (!v) {
        errLog("unexpected __tdxType value: %j", v);
        errList.push("missing __tdxType");
      } else {
        // Ensure type is an array.
        v = [].concat(v);
        delete schema.__tdxType;
        // The base type is the first element and should be a valid mongoose type
        schema.type = v[0].toLowerCase();
        if (schema.type === "object") {
          schema = {};
        } else if (schema.type === "array") {
          schema = [];
        } else {
          schema.type = validateMongooseType(vocab, schema.type, errList);
        }
      }
    } else if (Array.isArray(v)) {
      // Array
      if (v.length > 1) {
        errList.push(`invalid array type specification for key: ${k}`);
      } else if (v.length > 0) {
        if (typeof v[0] === "string") {
          // Array of single type.
          schema[k] = [{type: validateMongooseType(vocab, v[0], errList)}];
        } else {
          // Array of sub-document spec.
          const subDoc = schemaToMongoose(vocab, v[0], errList, forDisplay);
          // Prevent object sub-documents from being assigned an _id property.
          if (!subDoc.type || typeof subDoc.type !== "string") {
            subDoc._id = false;
          }
          schema[k] = [subDoc];
        }
      }
    } else if (typeof v === "object") {
      // Sub-document
      schema[k] = schemaToMongoose(vocab, v, errList, forDisplay);
    } else if (typeof v !== "string") {
      // TODO - review
      // Don't treat this as an error, just ignore.
      // Seems to be caused be some legacy schema definitions with { default: true, required: true } etc.
      // errList.push("invalid schema definition - unexpected type for key '" + k + "': " + (typeof v));
      errLog(`invalid schema definition - unexpected type for key '${k}' : ${typeof v}`);
      delete schema[k];
    } else {
      schema[k] = {type: validateMongooseType(vocab, v, errList)};
    }
  });

  return schema;
};

const definitionToMongoose = function(name, tdxSchema) {
  const uniqueIndex = tdxSchema.uniqueIndex;
  const indexes = tdxSchema.nonUniqueIndexes;

  // Convert any given indexes to mongodb format
  //
  // Because these dynamic schema definitions are stored in mongodb as documents,
  // we have to store them in a backwards fashion as mongodb does not allow
  // key names to contain periods. For example, if the index was saved
  // as { "address.postcode": 1 } mongodb would complain about "address.postcode".
  //
  // So we store indexes (which are likely to contain dots e.g. if indexing
  // on nested document) in the form:
  //
  // { "asc": "my.nested.key" } or { "desc": "address.postcode" }
  //
  // Here we convert this stored format to mongodb format so the index can be created.
  //
  // The 'indexes' parameter should be an array of arrays of index specifiers, e.g.
  // the following specifies two indexes, one is a compound index on account and order,
  // the other is an individual index on completedAt:
  //
  // [ [ { "asc": "account" }, { "asc": "order" } ], [ {"asc": "completedAt" } ] ]
  //
  // this should translate to a single array of objects, e.g.:
  //
  // [ { account: 1 , order: 1 }, { completedAt: 1} ]
  //
  const nonUniqueIndexes = [];
  _.forEach(indexes, function(nonUniqueIndex) {
    if (nonUniqueIndex.length > 0) {
      nonUniqueIndexes.push(indexToMongoose(nonUniqueIndex));
    }
  });
  log("translated non unique indexes for %s from %j to %j", name, indexes, nonUniqueIndexes);

  // Do the same for the unique index.
  // Use the 'uniqueIndex' parameter to set the PK of the schema.
  // Do this rather than use the built-in mongoose specification in order
  // to support compound indexes, where the order of fields is important.
  //
  // See comment above for index format explanation.
  const primaryIndex = indexToMongoose(uniqueIndex);
  log("translated primary index for %s from %j to %j", name, uniqueIndex, primaryIndex);

  //
  // Remove all index information from the schema, as we set the indexes
  // manually below.
  //
  log("transforming schema for %s: %j", tdxSchema.id, tdxSchema.dataSchema);
  const errList = [];
  let mongooseSchema;
  if (tdxSchema.mode === constants.legacyTDXSchema) {
    mongooseSchema = schemaToMongoose(null, _.cloneDeep(tdxSchema.dataSchema), errList, false);
  } else {
    // Fix-up vocabulary types to base JSON types.
    const props = _.cloneDeep(tdxSchema.dataSchema.properties);
    resolveVocabulary(tdxSchema.vocabulary, props);

    const pureSchema = {
      $schema: "http://json-schema.org/draft-04/schema#",
      type: "object",
    };

    _.extend(pureSchema, tdxSchema.dataSchema, {properties: props});

    try {
      mongooseSchema = jsonSchemaConverter(null, pureSchema);
    } catch (err) {
      errList.push(err.message);
    }
  }

  if (errList.length) {
    throw new Error(`invalid mongoose schema: ${errList.join(",")}`);
  }

  log("schema transformed to %j", mongooseSchema);

  return {
    schema: mongooseSchema,
    uniqueIndex: primaryIndex,
    nonUniqueIndexes: nonUniqueIndexes,
  };
};

const createDefaultForType = function(type) {
  let val;
  switch (type) {
    case "string":
      val = "";
      break;
    case "number":
      val = 0;
      break;
    case "date":
      val = new Date();
      break;
    case "boolean":
      val = false;
      break;
    case "object":
      val = {};
      break;
    case "array":
      val = [];
      break;
    default:
      val = null;
      break;
  }
  return val;
};

const createDocumentStructure = function(schema) {
  let obj = {};
  _.forOwn(schema, (v, k) => {
    if (typeof v === "object") {
      if (!v.type || v.type === "object") {
        if (v.properties && typeof v.properties === "object") {
          // Create object types.
          obj[k] = createDocumentStructure(v.properties);
        } else {
          obj[k] = {};
        }
      } else if (v.type === "array") {
        // Create array types.
        if (v.items) {
          // Attempt to create a placeholder first item?
          // obj[k] = [createDocumentStructure(v.items)];
          obj[k] = [];
        } else {
          obj[k] = [];
        }
      } else if (typeof v.type === "string") {
        // Not object definition or array.
        obj[k] = createDefaultForType(v.type);
      }
    } else if (k === "type" && typeof v === "string") {
      // Simplify native types.
      obj = createDefaultForType(v);
    }
  });
  return obj;
};

const documentFromSchema = function(jsonSchema) {
  // const jsonSchema = definitionToJSONSchema(schema);
  // if (schema.vocabulary) {
  //   resolveVocabulary(schema.vocabulary, jsonSchema.properties);
  // }

  const structure = createDocumentStructure(jsonSchema.properties);

  const ajv = new Ajv({useDefaults: true});
  const validate = ajv.compile(jsonSchema);
  const trueDefaults = {};
  validate(trueDefaults);

  return _.extend(structure, trueDefaults);
};

const jsonSchemaFromMongoose = function(schema, obj) {
  _.forOwn(schema, (v, k) => {
    if (v instanceof Array) {
      obj[k] = {
        type: "array",
        items: v[0] || {type: "object"},
      };
    } else if (typeof v === "object") {
      if (v.type && typeof v.type === "string") {
        obj[k] = {type: v.type.toLowerCase()};
      } else {
        obj[k] = {
          type: "object",
          properties: {},
        };
        jsonSchemaFromMongoose(v, obj[k].properties);
      }
    } else {
      obj[k] = {
        type: v,
      };
    }
  });
};

const jsonSchemaFromSimple = function(schema) {
  const obj = {};
  _.forOwn(schema, (v, k) => {
    if (v instanceof Array) {
      obj[k] = {
        type: "array",
      };
      if (v.length) {
        const itemSchema = v[0];
        let itemJSONSchema;
        if (typeof itemSchema === "string") {
          // Sub-documents are a basic type.
          itemJSONSchema = {type: itemSchema};
        } else {
          itemJSONSchema = {
            type: "object",
            properties: jsonSchemaFromSimple(itemSchema),
          };
        }
        obj[k].items = itemJSONSchema;
      }
    } else if (typeof v === "object") {
      if (v.type && typeof v.type === "string") {
        obj[k] = {type: v.type};
      } else {
        obj[k] = {
          type: "object",
          properties: jsonSchemaFromSimple(v),
        };
      }
    } else {
      // This is a basic type
      obj[k] = {
        type: v,
      };
    }
  });

  return obj;
};

const storeType = function(v, obj, resolveVocab) {
  if (resolveVocab) {
    const type = _.first(v.__tdxType);
    if (type === "date") {
      obj.type = "string";
      obj.format = "date-time";
    } else {
      obj.type = type;
    }
  } else {
    obj.type = _.last(v.__tdxType);
  }
};

const jsonSchemaFromTDX = function(schema, errList, resolveVocab = false) {
  errList = errList || [];
  const obj = {};
  _.forOwn(schema, (v, k) => {
    if (k === "__tdxType") {
      storeType(schema, obj, resolveVocab);
      return false;
    } else if (reservedFieldNames.indexOf(k) >= 0) {
      errLog("invalid field name (reserved): %s", k);
      errList.push(`invalid field name (reserved): ${k}`);
    } else if (v instanceof Array) {
      if (v.length === 0) {
        // Mixed type.
        obj[k] = {type: "array"};
      } else {
        let itemJSONSchema;
        const itemSpec = jsonSchemaFromTDX(v[0], errList, resolveVocab);
        if (itemSpec && typeof itemSpec.type === "string") {
          itemJSONSchema = itemSpec;
        } else {
          itemJSONSchema = {
            type: "object",
            properties: itemSpec,
          };
        }
        obj[k] = {
          type: "array",
          items: itemJSONSchema,
        };
      }
    } else if (typeof v === "object") {
      if (_.isEmpty(v)) {
        obj[k] = {};
      } else if (v.__tdxType) {
        obj[k] = {};
        storeType(v, obj[k], resolveVocab);
      } else {
        obj[k] = {
          type: "object",
          properties: jsonSchemaFromTDX(v),
        };
      }
    } else {
      log("unexpected schema format %s:%s", k, v);
    }
  });

  return obj;
};

const definitionToJSONSchema = function(schemaDefinition) {
  if (!schemaDefinition.mode || schemaDefinition.mode === constants.legacyTDXSchema) {
    return {
      type: "object",
      properties: jsonSchemaFromTDX(schemaDefinition.dataSchema),
    };
  } else {
    return _.cloneDeep(schemaDefinition.dataSchema);
  }
};

/**
 * Reduces a JSONSchema to simplified form and checks for reserved property names.
 * n.b. Will lose meta information (defaults, required etc).
 * @param  {} schema
 * @param  {} errList
 */
const simplifyJSONSchema = function(schema, errList) {
  errList = errList || [];
  let obj = {};
  _.forOwn(schema, (v, k) => {
    if (typeof v === "object") {
      if (reservedFieldNames.indexOf(k) >= 0) {
        errLog("invalid field name (reserved): %s", k);
        errList.push(`invalid field name (reserved): ${k}`);
      } else if (!v.type || v.type === "object") {
        if (v.properties && typeof v.properties === "object") {
          // Simplify object types.
          obj[k] = simplifyJSONSchema(v.properties);
        } else {
          obj[k] = {};
        }
      } else if (v.type === "array") {
        // Simplify array types.
        if (v.items && v.items.type) {
          let itemsSimplified;
          if (v.items.type === "object") {
            itemsSimplified = simplifyJSONSchema(v.items.properties || {});
          } else {
            itemsSimplified = v.items.type;
          }
          obj[k] = [itemsSimplified];
        } else {
          obj[k] = [];
        }
      } else if (typeof v.type === "string") {
        // Not object definition or array.
        obj[k] = v.type;
      }
    } else if (k === "type" && typeof v === "string") {
      // Simplify native types.
      obj = v;
    }
  });
  return obj;
};

const simplifyTDXSchema = function(schema, errList) {
  errList = errList || [];
  let obj = {};
  _.forOwn(schema, (v, k) => {
    if (k === "__tdxType") {
      obj = _.last(v);
      return false;
    } else if (reservedFieldNames.indexOf(k) >= 0) {
      errLog("invalid field name (reserved): %s", k);
      errList.push(`invalid field name (reserved): ${k}`);
    } else if (typeof v === "string") {
      obj[k] = v;
    } else if (v instanceof Array) {
      if (v.length === 0) {
        // Mixed type.
        obj[k] = [];
      } else {
        obj[k] = [simplifyTDXSchema(v[0], errList)];
      }
    } else if (typeof v === "object") {
      if (_.isEmpty(v)) {
        obj[k] = {};
      } else if (v.__tdxType) {
        obj[k] = _.last(v.__tdxType);
      } else {
        obj[k] = simplifyTDXSchema(v, errList);
      }
    } else {
      log("unexpected schema format %s:%s", k, v);
    }
  });

  return obj;
};

const simpleSchemaMetaSchema = function(vocab) {
  const typeEnum = _.uniq(_.map(vocab, "id"));

  const tdxSchema = {
    title: "tdx-schema",
    type: "object",
    patternProperties: {
      "^.*$": {
        anyOf: [
          {$ref: "#"},
          {
            type: "string",
            enum: typeEnum,
          },
          {
            type: "array",
            items: {
              anyOf: [
                {$ref: "#"},
                {
                  type: "string",
                  enum: typeEnum,
                },
              ],
            },
          },
        ],
      },
    },
    additionalProperties: false,
  };

  return tdxSchema;
};

const jsonSchemaMetaSchema = function(vocab) {
  // Load the core schema definition and build a list of references from the definitions.
  const tdxSchema = getJSONSchemaDefinition();

  // Convert the vocabulary items to JSONSchema.
  _.forEach(vocab, (v) => {
    tdxSchema.definitions.simpleTypes.enum.push(v.id);
  });

  tdxSchema.definitions.simpleTypes.enum = _.uniq(tdxSchema.definitions.simpleTypes.enum);

  return tdxSchema;
};

const findVocab = function(vocab, vocabHash, id, errList) {
  const found = _.find(vocab, (v) => v.id === id);
  if (!found) {
    errList.push(`failed to find vocabulary ${id}`);
  } else {
    vocabHash[id] = _.pick(found.toObject ? found.toObject() : found, ["id", "basedOn"]);
  }
  return found;
};

const extractSchemaVocabulary = function(vocab, schema, vocabHash, errList) {
  errList = errList || [];
  let obj = {};
  _.forOwn(schema, (v, k) => {
    if (typeof v === "object") {
      if (reservedFieldNames.indexOf(k) >= 0) {
        errLog("invalid field name (reserved): %s", k);
        errList.push(`invalid field name (reserved): ${k}`);
      } else if ((!v.type || v.type === "object") && v.properties && typeof v.properties === "object") {
        findVocab(vocab, vocabHash, "object", errList);
        // Extract object types.
        obj[k] = extractSchemaVocabulary(vocab, v.properties, vocabHash, errList);
      } else if (v.type === "array") {
        findVocab(vocab, vocabHash, "array", errList);
        // Extract array types.
        if (v.items) {
          let itemVocab;
          if (v.items.type === "object" && v.items.properties) {
            itemVocab = extractSchemaVocabulary(vocab, v.items.properties, vocabHash, errList);
          } else {
            itemVocab = findVocab(vocab, vocabHash, v.items.type, errList);
          }
          obj[k] = [itemVocab];
        } else {
          obj[k] = [];
        }
      } else if (typeof v.type === "string") {
        // Not object definition or array.
        findVocab(vocab, vocabHash, v.type, errList);
        obj[k] = v.type;
      }
    } else if (k === "type" && typeof v === "string") {
      // Native types.
      findVocab(vocab, vocabHash, v, errList);
      obj = v;
    }
  });
  return obj;
};

/**
 * Resolves all non-native vocabulary types.
 * n.b. mutates schema
 * @param  {} vocab
 * @param  {} schema
 */
const resolveVocabulary = function(vocab, schema) {
  _.forOwn(schema, (v) => {
    if (typeof v === "object") {
      if (!v.type || v.type === "object") {
        if (v.properties && typeof v.properties === "object") {
          // Resolve object types.
          resolveVocabulary(vocab, v.properties);
        }
      } else if (v.type === "array") {
        // Extract array types.
        if (typeof v.items === "object") {
          resolveVocabulary(vocab, {items: v.items});
        }
      } else if (typeof v.type === "string") {
        // Not object definition or array.
        if (!vocab[v.type]) {
          throw new Error(`type '${v.type}' not in vocabulary`);
        }
        // log("vocab lookup for %s is %s", v.type, _.first(vocab[v.type].basedOn) || vocab[v.type].id);
        v.type = _.first(vocab[v.type].basedOn) || vocab[v.type].id;
      }
    }
  });
};

const schemaForDisplay = function(schemaDefinition) {
  let jsonSchema;
  if (!schemaDefinition.mode || schemaDefinition.mode === constants.legacyTDXSchema) {
    // In legacy mode => convert from TDX to simple json schema.
    const convert = simplifyTDXSchema(schemaDefinition.dataSchema || {});
    jsonSchema = JSON.stringify(convert, null, 2);
  } else if (schemaDefinition.mode === constants.simpleSchema) {
    // In simplified schema mode, convert JSONSchema to simple format.
    const convert = simplifyJSONSchema((schemaDefinition.dataSchema && schemaDefinition.dataSchema.properties) || {});
    jsonSchema = JSON.stringify(convert, null, 2);
  } else {
    // In full JSON schema mode.
    jsonSchema = JSON.stringify(schemaDefinition.dataSchema || {type: "object", properties: {}}, null, 2);
  }
  return jsonSchema;
};

export default {
  definitionToMongoose: definitionToMongoose,
  schemaToMongoose: function(vocab, tdxSchema, errList, forDisplay) {
    return schemaToMongoose(vocab || [], _.cloneDeep(tdxSchema), errList, forDisplay);
  },
  schemaToTDX: function(vocab, mongooseSchema, errList) {
    return schemaToTDX(vocab || [], _.cloneDeep(mongooseSchema), errList);
  },
  indexToMongoose: indexToMongoose,
  indexesToMongoose: indexesToMongoose,
  indexToTDX: indexToTDX,
  indexesToTDX: indexesToTDX,

  definitionToJSONSchema,
  simpleSchemaMetaSchema,
  jsonSchemaMetaSchema,
  jsonSchemaFromMongoose,
  jsonSchemaFromSimple,
  jsonSchemaFromTDX,
  documentFromSchema,
  extractSchemaVocabulary,
  resolveVocabulary,
  simplifyJSONSchema,
  simplifyTDXSchema,
  schemaForDisplay,
};



// WEBPACK FOOTER //
// ./src/schema-utils.js